import { useState, useRef, useEffect, useCallback } from "react";
import { Text, Group, Stack, Paper, Divider, Box } from "@mantine/core";
import {
  IconPencil,
  IconCopy,
  IconTrash,
  IconChevronUp,
  IconChevronDown,
} from "@tabler/icons-react";
import { Input, Tooltip, Button } from "@onengine/component-library";
import { useFormStore } from "@/store/form-builder";
import { QuestionList } from "./QuestionList/QuestionList";
import { INLINE_HIT_AREA } from "../../data/builderLabels";
import type { FormSection } from "../../types/form.types";

import styles from "./SectionEditior.module.scss";

interface Props {
  section: FormSection;
  pageId: string;
  isExpanded: boolean;
  onToggle: () => void;
}

function InlineEditInput({
  value,
  onChange,
  onClose,
  size = "small",
  placeholder,
}: {
  value: string;
  onChange: (val: string) => void;
  onClose: () => void;
  size?: "small" | "large";
  placeholder?: string;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const input = wrapperRef.current?.querySelector("input");
    if (input) input.focus();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Escape") onClose();
  };

  return (
    <div ref={wrapperRef} onKeyDown={handleKeyDown}>
      <Input
        value={value}
        onChange={onChange}
        size={size}
        placeholder={placeholder}
        className={styles.inputStyle}
      />
    </div>
  );
}

export function SectionEditor({ section, pageId, isExpanded, onToggle }: Props) {
  const {
    updateSection,
    duplicateSection,
    deleteSection,
    pages,
    currentPageIndex,
    selectedSectionId,
    setSelectedSectionId,
  } = useFormStore();
  const isSelected = selectedSectionId === section.id;
  const [editingName, setEditingName] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const pg = pages[currentPageIndex];
  const canDelete = pg && pg.sections.length > 1;

  const closeNameEdit = useCallback(() => setEditingName(false), []);
  const closeDescEdit = useCallback(() => setEditingDesc(false), []);

  return (
    <Stack
      gap={8}
      mb="lg"
      data-section-id={section.id}
      onClick={() => setSelectedSectionId(section.id)}
      style={{ cursor: "default" }}
    >
      <Group gap={4} wrap="nowrap">
        <Paper
          px="md"
          py={5}
          radius="xl"
          withBorder
          className={styles.namePill}
          style={{
            gap: 6,
            borderColor: isSelected ? "#004ddb" : "#cfd4dd",
            borderWidth: 1,
            background: isSelected ? "white" : undefined,
            transition: "border-color 0.15s, background 0.15s",
          }}
        >
          {editingName ? (
            <InlineEditInput
              value={section.name}
              onChange={(val) => updateSection(pageId, section.id, { name: val })}
              onClose={closeNameEdit}
              size="small"
            />
          ) : (
            <Text
              size="sm"
              fw={500}
              c={isSelected ? "#004ddb" : "#363d4d"}
              className={styles.nameText}
              style={{ cursor: "pointer" }}
              onClick={() => setEditingName(true)}
            >
              {section.name}
            </Text>
          )}
          {!editingName && (
            <Button
              type="icon"
              variant="tertiary"
              size="small"
              iconLeft={<IconPencil size={20} />}
              htmlAttributes={{ "aria-label": "Rename section", ...INLINE_HIT_AREA }}
              onClick={() => setEditingName(true)}
            />
          )}
        </Paper>
        <Divider style={{ flex: 1 }} color="#cfd4dd" />
        <Tooltip content="Duplicate">
          <Button
            type="icon"
            variant="tertiary"
            size="small"
            iconLeft={<IconCopy size={24} />}
            htmlAttributes={{ "aria-label": "Duplicate section" }}
            onClick={() => duplicateSection(pageId, section.id)}
          />
        </Tooltip>
        <Tooltip content="Delete">
          <Button
            type="icon"
            variant="tertiary"
            size="small"
            isDisabled={!canDelete}
            iconLeft={<IconTrash size={24} />}
            htmlAttributes={{ "aria-label": "Delete section" }}
            onClick={canDelete ? () => deleteSection(pageId, section.id) : undefined}
          />
        </Tooltip>
        <Button
          type="icon"
          variant="tertiary"
          size="small"
          iconLeft={isExpanded ? <IconChevronUp size={24} /> : <IconChevronDown size={24} />}
          htmlAttributes={{ "aria-label": isExpanded ? "Collapse section" : "Expand section" }}
          onClick={onToggle}
        />
      </Group>

      {isExpanded && (
        <>
          <Box pl={4} mt={4}>
            {editingDesc ? (
              <Box maw={400}>
                <InlineEditInput
                  value={section.description}
                  onChange={(val) => updateSection(pageId, section.id, { description: val })}
                  onClose={closeDescEdit}
                  placeholder="Add a description..."
                />
              </Box>
            ) : (
              <Text
                size="sm"
                c="var(--text-weak, #646D7E)"
                fs={"normal"}
                style={{ cursor: "pointer", fontWeight: 500 }}
                onClick={() => setEditingDesc(true)}
              >
                {section.description || "Add a description for this section..."}
              </Text>
            )}
          </Box>

          <QuestionList scope={{ pageId, sectionId: section.id }} questions={section.questions} />
        </>
      )}
    </Stack>
  );
}
